// Client tipado de `loan-offerings-business` (SureKeep F1 · componente 07 · pista Loan).
// EJE BACKEND (ARQUITECTURA_FRONT_PROPUESTA.md §7): una función por operationId del OpenAPI.
// Dueño de esta carpeta = quien trabaja el lambda (Carlos) → agregar acá no colisiona con nadie.
//
// El lambda es el catálogo comercial del SOFOM: planes de crédito + financieras + M:N retailer↔plan
// + simulador. El backoffice consume los endpoints PÚBLICOS (auth backoffice + RBAC
// `loan.creditPlan.*` / `loan.financier.*`); los privados (`?tenantId=`) son para otros lambdas.
//
// TIPOS — igual que loan-config/retail-*: los `*.gen.ts` dan las rutas; las entidades vienen de
// `@fiado/type-kit` por deep-path con `import type` (cero costo en el bundle).
import type { CreditPlanResponse } from '@fiado/type-kit/bin/loanOfferings/dtos/responses/CreditPlanResponse';
import type { CreditPlanStatusEnum } from '@fiado/type-kit/bin/loanOfferings/enums/CreditPlanStatusEnum';
import type {
    ChangePromotionStatusRequest,
    ChangeStorePolicyStatusRequest,
    CreatePromotionRequest,
    CreateStorePolicyRequest,
    CreditPlanLogFeedQuery,
    CreditPlanLogPageResponse,
    CreditPlanLogQuery,
    PromotionListResponse,
    PromotionLogEntryResponse,
    PromotionResponse,
    PromotionStatusWire,
    ScopeCatalogResponse,
    StorePolicyLogFeedQuery,
    StorePolicyLogPageResponse,
    StorePolicyLogQuery,
    StorePolicyResponse,
    StorePolicyStatusWire,
    UpdateCreditPlanRequestWire,
    UpdatePromotionRequest,
    UpdateStorePolicyRequest,
} from './contract';
import type {
    ChangeCohortStatusRequest,
    CohortCatalogResponse,
    CohortListResponse,
    CohortLogFeedQuery,
    CohortLogPageResponse,
    CohortLogQuery,
    CohortResponse,
    CohortStatusWire,
    CreateCohortRequest,
    TestCohortRequest,
    TestCohortResponse,
    UpdateCohortRequest,
} from './cohorts.contract';
import type {
    ChangeSegmentStatusRequest,
    CreateSegmentRequest,
    SegmentationRecountRunsResponse,
    SegmentationRecountStartResponse,
    SegmentLogFeedQuery,
    SegmentLogPageResponse,
    SegmentLogQuery,
    SegmentResponse,
    SegmentStatusWire,
    SegmentUsageResponse,
    TestCustomerSegmentsRequest,
    TestCustomerSegmentsResponse,
    UpdateSegmentRequest,
} from './segments.contract';
import type {
    StoreQuoterPlan,
    StoreQuoterSimulation,
    StoreQuoterSimulationQuery,
} from './quoter.contract';
import { apiFetch } from '../../client';

const BASE_URL = import.meta.env.VITE_API_LOAN_OFFERINGS_BASE_URL;

/** Toda llamada de este servicio va a SU base path — no al del rbac. */
function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    return apiFetch<T>(path, { ...init, baseUrl: BASE_URL });
}

export type { CreditPlanResponse, UpdateCreditPlanRequestWire };

/**
 * `publicListCreditPlans` — GET /credit-plans. Catálogo de planes ordenado (Bronce/Plata/Oro/…).
 * Alimenta la pantalla Políticas (tab Estándar). Requiere permiso `loan.creditPlan.read`.
 * `apiFetch` desenvuelve el envelope `{ code, data, msg }` → devuelve el array directo.
 */
export function listCreditPlans(): Promise<CreditPlanResponse[]> {
    return call<CreditPlanResponse[]>('/credit-plans');
}

/**
 * `publicUpdateCreditPlan` — PUT /credit-plans/{planId}. Parche parcial: solo los campos presentes
 * cambian. El backend auto-incrementa `version` y devuelve el plan actualizado. Requiere permiso
 * `loan.creditPlan.update`.
 */
export function updateCreditPlan(
    planId: string,
    body: UpdateCreditPlanRequestWire,
): Promise<CreditPlanResponse> {
    return call<CreditPlanResponse>(`/credit-plans/${encodeURIComponent(planId)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/**
 * `publicUpdateCreditPlanStatus` — PUT /credit-plans/{planId}/status. Activa (`ACTIVE`) / inactiva
 * (`INACTIVE`) un plan. Requiere permiso `loan.creditPlan.update`.
 */
export function changeCreditPlanStatus(
    planId: string,
    status: CreditPlanStatusEnum,
): Promise<CreditPlanResponse> {
    return call<CreditPlanResponse>(`/credit-plans/${encodeURIComponent(planId)}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
    });
}

/** Los filtros de una página de bitácora (`day`, `limit`, `cursor`). */
interface LogPageQuery {
    day?: string;
    limit?: number;
    cursor?: string;
}

/** Los filtros de bitácora como query string; vacío cuando no hay ninguno. */
function logQuery(params: LogPageQuery): string {
    const query = new URLSearchParams();
    if (params.day !== undefined) query.set('day', params.day);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.cursor !== undefined) query.set('cursor', params.cursor);
    const search = query.toString();
    return search ? `?${search}` : '';
}

/**
 * `publicListCreditPlanLog` — GET /credit-plans/log. El feed de UN día con los asientos de TODOS los
 * planes (tab Auditoría). Sin `day` responde el día en curso en hora de CDMX. Requiere
 * `loan.creditPlan.read`.
 */
export function listCreditPlanLog(
    params: CreditPlanLogFeedQuery = {},
): Promise<CreditPlanLogPageResponse> {
    return call<CreditPlanLogPageResponse>(`/credit-plans/log${logQuery(params)}`);
}

/**
 * `publicListCreditPlanLogByPlan` — GET /credit-plans/{planId}/log. Los asientos de bitácora de UN
 * plan, sin tope de fecha y paginados por cursor. Requiere permiso `loan.creditPlan.read`; 404 si
 * el plan no existe.
 */
export function listCreditPlanLogByPlan(
    planId: string,
    params: CreditPlanLogQuery = {},
): Promise<CreditPlanLogPageResponse> {
    return call<CreditPlanLogPageResponse>(
        `/credit-plans/${encodeURIComponent(planId)}/log${logQuery(params)}`,
    );
}

// ---------------------------------------------------------------------------------------------
// PROMOCIONES (fase A) — alimentan la pantalla `/credit/promotions`. Tipos en `./contract`.
// ---------------------------------------------------------------------------------------------

/**
 * `publicListPromotions` — GET /promotions. Con `status` filtra por estado persistido.
 * Devuelve el envelope: cada fila trae el segmento ya resuelto y `truncated` dice si vino cortada.
 */
export function listPromotions(status?: PromotionStatusWire): Promise<PromotionListResponse> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return call<PromotionListResponse>(`/promotions${query}`);
}

/** `publicGetPromotion` — GET /promotions/{promotionId}. */
export function getPromotion(promotionId: string): Promise<PromotionResponse> {
    return call<PromotionResponse>(`/promotions/${encodeURIComponent(promotionId)}`);
}

/** `publicCreatePromotion` — POST /promotions. La promo nace en DRAFT. */
export function createPromotion(body: CreatePromotionRequest): Promise<PromotionResponse> {
    return call<PromotionResponse>('/promotions', { method: 'POST', body: JSON.stringify(body) });
}

/**
 * `publicUpdatePromotion` — PUT /promotions/{promotionId}. Parche parcial; `effect` se REEMPLAZA
 * entero cuando viene. Solo DRAFT y PUBLISHED admiten edición (409 en los otros dos estados).
 */
export function updatePromotion(
    promotionId: string,
    body: UpdatePromotionRequest,
): Promise<PromotionResponse> {
    return call<PromotionResponse>(`/promotions/${encodeURIComponent(promotionId)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/**
 * `publicChangePromotionStatus` — PUT /promotions/{promotionId}/status. Publica, pausa, reanuda o
 * vence. Las transiciones que el backend no admite responden 409.
 */
export function changePromotionStatus(
    promotionId: string,
    status: PromotionStatusWire,
    reason?: string,
): Promise<PromotionResponse> {
    const body: ChangePromotionStatusRequest = reason ? { status, reason } : { status };
    return call<PromotionResponse>(`/promotions/${encodeURIComponent(promotionId)}/status`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/**
 * `publicGetPromotionScopeCatalog` — GET /promotions/scope-catalog. Las tiendas y los productos de
 * todas las cadenas del tenant en UNA lectura; `truncated` avisa que el lambda cortó el barrido.
 */
export function getScopeCatalog(): Promise<ScopeCatalogResponse> {
    return call<ScopeCatalogResponse>('/promotions/scope-catalog');
}

/**
 * `publicListPromotionLog` — GET /promotions/log. La bitácora se particiona por mes: sin `month`
 * el lambda devuelve el mes en curso. Formato `yyyy-mm`.
 */
export function listPromotionLog(month?: string): Promise<PromotionLogEntryResponse[]> {
    const query = month ? `?month=${encodeURIComponent(month)}` : '';
    return call<PromotionLogEntryResponse[]>(`/promotions/log${query}`);
}

// ---------------------------------------------------------------------------------------------
// SEGMENTOS — la pantalla `/credit/segments` y los selectores de Políticas y Promociones. NO hay
// DELETE: el bote de basura archiva con `PUT /segments/{id}/status`.
// ---------------------------------------------------------------------------------------------

/** `publicListSegments` — GET /segments. Con `status` filtra por estado persistido. */
export function listSegments(status?: SegmentStatusWire): Promise<SegmentResponse[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return call<SegmentResponse[]>(`/segments${query}`);
}

/** `publicCreateSegment` — POST /segments. */
export function createSegment(body: CreateSegmentRequest): Promise<SegmentResponse> {
    return call<SegmentResponse>('/segments', { method: 'POST', body: JSON.stringify(body) });
}

/**
 * `publicUpdateSegment` — PUT /segments/{segmentId}. Parche parcial. El segmento default solo
 * acepta cambio de nombre: cualquier otro campo responde 422 `DEFAULT_SEGMENT_NOT_EDITABLE`.
 */
export function updateSegment(
    segmentId: string,
    body: UpdateSegmentRequest,
): Promise<SegmentResponse> {
    return call<SegmentResponse>(`/segments/${encodeURIComponent(segmentId)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/**
 * `publicChangeSegmentStatus` — PUT /segments/{segmentId}/status. Archiva o reactiva. Con
 * políticas o promociones publicadas apuntándole responde 409 `SEGMENT_IN_USE`.
 */
export function changeSegmentStatus(
    segmentId: string,
    status: SegmentStatusWire,
    reason?: string,
): Promise<SegmentResponse> {
    const body: ChangeSegmentStatusRequest = reason ? { status, reason } : { status };
    return call<SegmentResponse>(`/segments/${encodeURIComponent(segmentId)}/status`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/** `publicGetSegmentUsage` — GET /segments/{segmentId}/usage. Políticas y promociones que lo usan. */
export function getSegmentUsage(segmentId: string): Promise<SegmentUsageResponse> {
    return call<SegmentUsageResponse>(`/segments/${encodeURIComponent(segmentId)}/usage`);
}

/**
 * `publicTestCustomerSegments` — POST /segments/test. En qué segmentos cae un cliente y por qué.
 * 503 `LOAN_CREDIT_UNAVAILABLE` si loan-credit no entregó sus datos.
 */
export function testCustomerSegments(
    body: TestCustomerSegmentsRequest,
): Promise<TestCustomerSegmentsResponse> {
    return call<TestCustomerSegmentsResponse>('/segments/test', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/** `publicListSegmentsLog` — GET /segments/log. El feed de UN día con todos los segmentos. */
export function listSegmentsLog(params: SegmentLogFeedQuery = {}): Promise<SegmentLogPageResponse> {
    return call<SegmentLogPageResponse>(`/segments/log${logQuery(params)}`);
}

/** `publicGetSegmentLog` — GET /segments/{segmentId}/log. Historial de UN segmento, por cursor. */
export function listSegmentLog(
    segmentId: string,
    params: SegmentLogQuery = {},
): Promise<SegmentLogPageResponse> {
    return call<SegmentLogPageResponse>(
        `/segments/${encodeURIComponent(segmentId)}/log${logQuery(params)}`,
    );
}

/**
 * `publicStartSegmentationRecount` — POST /segmentation/recount. Abre una corrida en segundo plano;
 * 409 `RECOUNT_IN_PROGRESS` si ya hay una abierta.
 */
export function startSegmentationRecount(): Promise<SegmentationRecountStartResponse> {
    return call<SegmentationRecountStartResponse>('/segmentation/recount', { method: 'POST' });
}

/** `publicListSegmentationRecountRuns` — GET /segmentation/recount/runs. Las últimas 50. */
export function listSegmentationRecountRuns(): Promise<SegmentationRecountRunsResponse> {
    return call<SegmentationRecountRunsResponse>('/segmentation/recount/runs');
}

// ---------------------------------------------------------------------------------------------
// COHORTS — la pantalla `/credit/cohorts` y la composición de segmentos. NO hay DELETE: se archiva
// con `PUT /cohorts/{id}/status`.
// ---------------------------------------------------------------------------------------------

/** `publicListCohorts` — GET /cohorts. Sin `status` devuelve activos y archivados. */
export function listCohorts(status?: CohortStatusWire): Promise<CohortListResponse> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return call<CohortListResponse>(`/cohorts${query}`);
}

/** `publicGetCohortCatalog` — GET /cohorts/catalog. Los datos del cliente con los que se arma uno. */
export function getCohortCatalog(): Promise<CohortCatalogResponse> {
    return call<CohortCatalogResponse>('/cohorts/catalog');
}

/** `publicGetCohort` — GET /cohorts/{cohortId}. Con los segmentos activos que lo usan. */
export function getCohort(cohortId: string): Promise<CohortResponse> {
    return call<CohortResponse>(`/cohorts/${encodeURIComponent(cohortId)}`);
}

/** `publicCreateCohort` — POST /cohorts. Nace ACTIVE. */
export function createCohort(body: CreateCohortRequest): Promise<CohortResponse> {
    return call<CohortResponse>('/cohorts', { method: 'POST', body: JSON.stringify(body) });
}

/**
 * `publicUpdateCohort` — PUT /cohorts/{cohortId}. Parche parcial; sube `version`. 409
 * `COHORT_VERSION_CONFLICT` si alguien lo guardó primero.
 */
export function updateCohort(cohortId: string, body: UpdateCohortRequest): Promise<CohortResponse> {
    return call<CohortResponse>(`/cohorts/${encodeURIComponent(cohortId)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/**
 * `publicChangeCohortStatus` — PUT /cohorts/{cohortId}/status. Archiva o reactiva. Si lo usan
 * segmentos activos responde 409 `COHORT_IN_USE` con la lista en `data.segments`.
 */
export function changeCohortStatus(
    cohortId: string,
    status: CohortStatusWire,
    reason?: string,
): Promise<CohortResponse> {
    const body: ChangeCohortStatusRequest = reason ? { status, reason } : { status };
    return call<CohortResponse>(`/cohorts/${encodeURIComponent(cohortId)}/status`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/**
 * `publicTestCohort` — POST /cohorts/test. Prueba condiciones (aunque no estén guardadas) con un
 * cliente. 503 si el motor o loan-credit no responden.
 */
export function testCohort(body: TestCohortRequest): Promise<TestCohortResponse> {
    return call<TestCohortResponse>('/cohorts/test', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/** `publicListCohortsLog` — GET /cohorts/log. El feed de UN día con todos los cohorts. */
export function listCohortsLog(params: CohortLogFeedQuery = {}): Promise<CohortLogPageResponse> {
    return call<CohortLogPageResponse>(`/cohorts/log${logQuery(params)}`);
}

/** `publicGetCohortLog` — GET /cohorts/{cohortId}/log. Historial de UN cohort, por cursor. */
export function listCohortLog(
    cohortId: string,
    params: CohortLogQuery = {},
): Promise<CohortLogPageResponse> {
    return call<CohortLogPageResponse>(
        `/cohorts/${encodeURIComponent(cohortId)}/log${logQuery(params)}`,
    );
}

// ---------------------------------------------------------------------------------------------
// POLÍTICAS ESPECIALES — alimentan el tab Especiales de `/credit/policies`. Tipos en `./contract`.
// Permisos RBAC: `loan.storePolicy.read` / `.create` / `.update`, con scope TENANT.
// ---------------------------------------------------------------------------------------------

/** `publicListStorePolicies` — GET /store-policies. Con `status` filtra por estado persistido. */
export function listStorePolicies(status?: StorePolicyStatusWire): Promise<StorePolicyResponse[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return call<StorePolicyResponse[]>(`/store-policies${query}`);
}

/** `publicGetStorePolicy` — GET /store-policies/{policyId}. */
export function getStorePolicy(policyId: string): Promise<StorePolicyResponse> {
    return call<StorePolicyResponse>(`/store-policies/${encodeURIComponent(policyId)}`);
}

/** `publicCreateStorePolicy` — POST /store-policies. La política nace en DRAFT. */
export function createStorePolicy(body: CreateStorePolicyRequest): Promise<StorePolicyResponse> {
    return call<StorePolicyResponse>('/store-policies', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/**
 * `publicUpdateStorePolicy` — PUT /store-policies/{policyId}. Parche parcial; `hardening` y
 * `easing` se REEMPLAZAN enteros cuando vienen. El backend sube `version` y responde 409 si otro
 * operador guardó primero.
 */
export function updateStorePolicy(
    policyId: string,
    body: UpdateStorePolicyRequest,
): Promise<StorePolicyResponse> {
    return call<StorePolicyResponse>(`/store-policies/${encodeURIComponent(policyId)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/**
 * `publicChangeStorePolicyStatus` — PUT /store-policies/{policyId}/status. Publica, pausa, reanuda
 * o vence. Las transiciones que el backend no admite responden 409; EXPIRED es terminal.
 */
export function changeStorePolicyStatus(
    policyId: string,
    status: StorePolicyStatusWire,
    reason?: string,
): Promise<StorePolicyResponse> {
    const body: ChangeStorePolicyStatusRequest = reason ? { status, reason } : { status };
    return call<StorePolicyResponse>(`/store-policies/${encodeURIComponent(policyId)}/status`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/** Los filtros de la bitácora de políticas como query string; vacío cuando no hay ninguno. */
function storePolicyLogQuery(params: StorePolicyLogFeedQuery): string {
    const query = new URLSearchParams();
    if (params.day !== undefined) query.set('day', params.day);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.cursor !== undefined) query.set('cursor', params.cursor);
    const search = query.toString();
    return search ? `?${search}` : '';
}

/**
 * `publicListStorePolicyLog` — GET /store-policies/log. El feed de UN día con los asientos de TODAS
 * las políticas. Sin `day` responde el día en curso en hora de Ciudad de México.
 */
export function listStorePolicyLog(
    params: StorePolicyLogFeedQuery = {},
): Promise<StorePolicyLogPageResponse> {
    return call<StorePolicyLogPageResponse>(`/store-policies/log${storePolicyLogQuery(params)}`);
}

/**
 * `publicListStorePolicyLogByPolicy` — GET /store-policies/{policyId}/log. Los asientos de UNA
 * política, sin tope de fecha y paginados por cursor. 404 si la política no existe.
 */
export function listStorePolicyLogByPolicy(
    policyId: string,
    params: StorePolicyLogQuery = {},
): Promise<StorePolicyLogPageResponse> {
    return call<StorePolicyLogPageResponse>(
        `/store-policies/${encodeURIComponent(policyId)}/log${storePolicyLogQuery(params)}`,
    );
}

/**
 * `publicListStoreQuoterPlans` — GET /stores/{storeId}/quoter/plans. Los planes activos que el
 * vendedor puede cotizar en su tienda. Requiere `loan.quoter.read` sobre esa tienda.
 */
export function listStoreQuoterPlans(storeId: string): Promise<StoreQuoterPlan[]> {
    return call<StoreQuoterPlan[]>(`/stores/${encodeURIComponent(storeId)}/quoter/plans`);
}

/**
 * `publicSimulateStoreQuoterPlan` — GET /stores/{storeId}/quoter/plans/{planId}/simulation. Cotiza
 * un plazo; los rechazos llegan como `ApiError` con su `code` y la cota en `data`.
 */
export function simulateStoreQuoterPlan(
    storeId: string,
    planId: string,
    params: StoreQuoterSimulationQuery,
): Promise<StoreQuoterSimulation> {
    const query = new URLSearchParams({
        productPriceCents: String(params.productPriceCents),
        downPaymentPct: String(params.downPaymentPct),
        termWeeks: String(params.termWeeks),
    });
    if (params.productSku) query.set('productSku', params.productSku);
    return call<StoreQuoterSimulation>(
        `/stores/${encodeURIComponent(storeId)}/quoter/plans/${encodeURIComponent(planId)}/simulation?${query.toString()}`,
    );
}
